import { useCallback, useState, useEffect, useRef } from "react";
import { storage as storageClient } from "../../../lib/storage/storage";
import { Keys } from "../../../lib/storage/keys";
import { api } from "../../../lib/api";
import { normalizeRules } from "../../../lib/rules/normalize";
import { sendToTerminal, debugLog } from "../../../lib/terminalLogger";
import type { ExportType } from "../../ExportModal/types";
import {
	buildLayoutPngPayload,
	calculateLayoutScale,
} from "../../ExportModal/services/layoutPayloadBuilder";
import {
	buildLayoutApiPayload,
	postLayoutExport,
} from "../../ExportModal/services/layoutApi";
import { corelCommunication } from "../../../lib/corel/communication";
import { getActionTimeout } from "../../../lib/corel/timeoutService";
import { COREL_PROGRESS_SILENCE_TIMEOUT_MS } from "../../../config";
import { useNotifications } from "../../feedback/NotificationProvider";

interface UseProductionExportActionParams {
	selectedPedido: any;
	selectedItem: any;
	selectedObjects: any[];
	itemPendencias: string[];
	onNavigateToExport: () => void; // Nova callback para navegar para página
	setShowExportLayoutModal: (value: boolean) => void;
	setShowLayoutOnlyPrompt: (value: boolean) => void;
	resolveCaminhoArquivo: (item: any) => string | null;
	monitoradorObjetos: any; // NOVO: instância do monitor
	onExportPrepareChange?: (state: {
		preparing: boolean;
		step: "sync" | "prepare";
		error: string | null;
	}) => void; // NOVO: callback
}

export function useProductionExportAction({
	selectedPedido,
	selectedItem,
	selectedObjects,
	itemPendencias,
	onNavigateToExport,
	setShowExportLayoutModal,
	setShowLayoutOnlyPrompt,
	resolveCaminhoArquivo,
	monitoradorObjetos,
	onExportPrepareChange,
}: UseProductionExportActionParams) {
	const [preparingExport, setPreparingExport] = useState(false);
	const [exportPrepareStep, setExportPrepareStep] = useState<
		"sync" | "prepare"
	>("sync");
	const [exportPrepareError, setExportPrepareError] = useState<string | null>(
		null,
	);

	// ✅ CORREÇÃO BUG DUPLO CLIQUE: Usar ref para lock imediato (state tem delay)
	// O useState é assíncrono - entre o clique e o setPreparingExport(true),
	// outro clique pode passar pelo if (preparingExport) return;
	const preparingExportRef = useRef(false);

	// ✅ CORREÇÃO: Timestamp do último clique para debounce adicional
	const lastClickTimestampRef = useRef<number>(0);
	const DEBOUNCE_MS = 300; // Ignorar cliques dentro de 300ms

	// ============================================================================
	// LIFECYCLE DO MONITOR
	// ============================================================================

	/**
	 * ⚠️ IMPORTANTE: Este hook NÃO gerencia o lifecycle do monitor.
	 *
	 * O monitor é iniciado na pipeline e parado quando OrderDetailsWindow desmonta.
	 * Este hook apenas utiliza o monitor que já está ativo.
	 */

	// ✅ CORREÇÃO BUG DUPLO CLIQUE: Reset do lock quando pedido/item muda ou componente desmonta
	useEffect(() => {
		// Reset ao montar com novo pedido/item
		preparingExportRef.current = false;
		lastClickTimestampRef.current = 0;
		setPreparingExport(false);

		sendToTerminal(
			"🔄 [EXPORT_PREP] Lock resetado (novo pedido/item ou montagem)",
			{
				orcId: selectedPedido?.orc_id,
				oitId: selectedItem?.oit_id,
			},
		);

		return () => {
			// Cleanup: garantir que lock é liberado ao desmontar
			preparingExportRef.current = false;
			sendToTerminal("🧹 [EXPORT_PREP] Lock liberado (unmount)");
		};
	}, [selectedPedido?.orc_id, selectedItem?.oit_id]);

	// Notificar mudanças de estado
	const notifyStateChange = useCallback(() => {
		if (onExportPrepareChange) {
			onExportPrepareChange({
				preparing: preparingExport,
				step: exportPrepareStep,
				error: exportPrepareError,
			});
		}
	}, [
		preparingExport,
		exportPrepareStep,
		exportPrepareError,
		onExportPrepareChange,
	]);

	useEffect(() => {
		notifyStateChange();
	}, [notifyStateChange]);

	const { notify } = useNotifications();

	// Função auxiliar para executar layout diretamente
	const executeLayoutDirectly = useCallback(
		async (savedData: any, targets: any[]) => {
			sendToTerminal(
				"🟢 [LAYOUT_DIRECT] Iniciando exportação de layout diretamente",
				{
					targetsCount: targets.length,
					orcId: savedData.pedido.orc_id,
					oitId: savedData.pedido.oit_id,
				},
			);

			try {
				const requestId = `layout_${Date.now()}_${Math.random()
					.toString(36)
					.slice(2, 9)}`;
				const idempotencyKey = `layout_${savedData.pedido.orc_id}_${savedData.pedido.oit_id
					}_${Date.now()}`;

				// 1. Build payload
				const payload = buildLayoutPngPayload(savedData, targets, requestId);

				// 2. Execute via webview
				const layoutResult = await new Promise((resolve, reject) => {
					let resolved = false;
					let maxTimeoutId: NodeJS.Timeout | null = null; // Hard limit (painel admin)
					let silenceTimeoutId: NodeJS.Timeout | null = null; // Reseta a cada progress

					// Helper para limpar ambos os timeouts
					const clearAllTimeouts = () => {
						if (maxTimeoutId) clearTimeout(maxTimeoutId);
						if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
					};

					// Helper para iniciar/resetar o silence timeout
					const resetSilenceTimeout = () => {
						if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
						const silenceMinutes = Math.round(
							COREL_PROGRESS_SILENCE_TIMEOUT_MS / 60000,
						);
						silenceTimeoutId = setTimeout(() => {
							if (!resolved) {
								resolved = true;
								clearAllTimeouts();
								sendToTerminal("⏱️ [PROD_LAYOUT:SILENCE_TIMEOUT]", {
									silenceMs: COREL_PROGRESS_SILENCE_TIMEOUT_MS,
									silenceMinutes,
								});
								reject(
									new Error(
										`Timeout: sem resposta do C# por ${silenceMinutes} minutos`,
									),
								);
							}
						}, COREL_PROGRESS_SILENCE_TIMEOUT_MS);
					};

					const responseHandler = (response: any) => {
						if (resolved) return;

						try {
							const status = response?.status;
							const type = response?.type;

							// Progresso - resetar silence timeout
							if (status === "progress" || status === "progress_update") {
								// ✅ Resetar silence timeout a cada progress recebido
								resetSilenceTimeout();
								return;
							}

							if (
								type === "export-complete" ||
								type === "layout-complete" ||
								status === "ok"
							) {
								resolved = true;
								clearAllTimeouts();
								resolve(response);
							} else if (
								type === "export-error" ||
								type === "layout-error" ||
								status === "error" ||
								status === "erro" ||
								status === "cancelled"
							) {
								resolved = true;
								clearAllTimeouts();
								const errorMsg =
									response?.error ||
									response?.message ||
									"Erro na exportação de layout";
								reject(new Error(errorMsg));
							}
						} catch (err) {
							// Ignorar erros de parsing
						}
					};

					// Buscar timeout do painel admin (fallback: 1h) - hard limit máximo
					const layoutTimeout = getActionTimeout(
						payload.action || "exportar-objetos-png-lite",
					);
					const maxTimeoutMinutes = Math.round(layoutTimeout / 60000);

					const sentRequestId = corelCommunication.sendActionGuarded(
						payload.action,
						payload.data,
						responseHandler,
						layoutTimeout, // timeout do painel admin
					);

					if (!sentRequestId) {
						reject(
							new Error("Falha ao enviar comando de exportação de layout"),
						);
						return;
					}

					// ✅ Timeout máximo absoluto (hard limit do painel admin) - NUNCA reseta
					maxTimeoutId = setTimeout(() => {
						if (!resolved) {
							resolved = true;
							clearAllTimeouts();
							sendToTerminal("⏱️ [PROD_LAYOUT:MAX_TIMEOUT]", {
								maxTimeoutMs: layoutTimeout,
								maxTimeoutMinutes,
							});
							reject(
								new Error(
									`Timeout: operação excedeu limite máximo de ${maxTimeoutMinutes} minutos`,
								),
							);
						}
					}, layoutTimeout);

					// ✅ Silence timeout (reseta a cada progress) - inicia imediatamente
					resetSilenceTimeout();
				});

				const filesCount =
					(layoutResult as any)?.result?.exportados?.length ||
					(layoutResult as any)?.result?.items?.length ||
					(layoutResult as any)?.items?.length ||
					0;

				// 3. Registrar no backend
				try {
					const layoutOutputDir = payload.data.pastaDestino;
					// Construir objeto de resultado no formato esperado por buildLayoutApiPayload
					const layoutResultForApi = {
						success: true,
						result: layoutResult,
						files: filesCount,
					};
					// Calcular escala dinâmica baseada na altura máxima do material
					const alturaMaximaCm = savedData?.regras?.altura_maxima_cm ?? null;
					const escalaUsada = calculateLayoutScale(alturaMaximaCm);

					const layoutApiPayload = buildLayoutApiPayload(
						savedData,
						layoutResultForApi,
						layoutOutputDir,
						escalaUsada, // Escala dinâmica baseada no material
						300, // DPI
					);

					if (layoutApiPayload) {
						await postLayoutExport(layoutApiPayload, idempotencyKey);
						sendToTerminal("✅ [LAYOUT_DIRECT] Layout registrado no backend");
					}
				} catch (apiError) {
					sendToTerminal(
						"⚠️ [LAYOUT_DIRECT] Erro ao registrar layout no backend (continuando)",
						{
							error:
								apiError instanceof Error ? apiError.message : String(apiError),
						},
					);
				}

				notify({
					type: "success",
					title: "Layout Exportado",
					message: `${filesCount} PNG(s) exportados com sucesso`,
					sticky: false,
				});

				sendToTerminal("✅ [LAYOUT_DIRECT] Layout exportado com sucesso", {
					filesCount,
				});
				return { success: true, files: filesCount };
			} catch (error) {
				const errorMsg = error instanceof Error ? error.message : String(error);
				sendToTerminal("❌ [LAYOUT_DIRECT] Erro ao exportar layout", {
					error: errorMsg,
				});

				notify({
					type: "error",
					title: "Erro ao Exportar Layout",
					message: errorMsg,
					sticky: false,
				});

				throw error;
			}
		},
		[notify],
	);

	const startProductionExport = useCallback(
		async (exportType?: ExportType) => {
			// ✅ CORREÇÃO BUG DUPLO CLIQUE: Log imediato para diagnóstico
			const clickTimestamp = Date.now();
			const timeSinceLastClick = clickTimestamp - lastClickTimestampRef.current;

			sendToTerminal("🔘 [EXPORT_PREP] CLICK DETECTED", {
				clickTimestamp,
				timeSinceLastClick,
				preparingExportState: preparingExport,
				preparingExportRef: preparingExportRef.current,
				selectedObjectsCount: selectedObjects?.length ?? 0,
				exportType: exportType || "both",
			});

			// ✅ CORREÇÃO 1: Debounce - ignorar cliques muito rápidos (ghost clicks)
			if (timeSinceLastClick < DEBOUNCE_MS) {
				sendToTerminal(
					"⚠️ [EXPORT_PREP] BLOCKED (debounce - clique muito rápido)",
					{
						timeSinceLastClick,
						DEBOUNCE_MS,
					},
				);
				return;
			}
			lastClickTimestampRef.current = clickTimestamp;

			// ✅ CORREÇÃO 2: Usar REF para lock imediato (state tem delay assíncrono)
			if (preparingExportRef.current) {
				sendToTerminal("⚠️ [EXPORT_PREP] BLOCKED (ref já em andamento)", {
					preparingExportRef: preparingExportRef.current,
					preparingExportState: preparingExport,
				});
				return;
			}

			// ✅ CORREÇÃO 3: Lock IMEDIATO via ref antes de qualquer await
			preparingExportRef.current = true;

			const caminho = resolveCaminhoArquivo(selectedItem);
			if (!caminho) {
				sendToTerminal("❌ [PRODUZIR:BLOCKED:NO_PATH]");
				preparingExportRef.current = false; // ✅ Liberar lock
				notify({
					type: "error",
					title: "Não foi possível iniciar a produção",
					message: "Caminho do arquivo indisponível.",
					sticky: false,
				});
				return;
			}

			setPreparingExport(true);
			setExportPrepareError(null);
			sendToTerminal("🟡 [EXPORT_PREP] START", {
				pedidoId: selectedPedido.orc_id,
				itemId: selectedItem.oit_id,
				exportType: exportType || "both",
				timestamp: Date.now(),
			});

			try {
				// ETAPA 1: OBTER OBJETOS (usar selectedObjects se disponível, senão fazer sync)
				setExportPrepareStep("sync");
				sendToTerminal("📡 [EXPORT_PREP] SYNC_START");

				let objetos: any[] = [];
				let sourceUsed: "selectedObjects" | "sync" = "sync";

				// ✅ CORREÇÃO: Usar selectedObjects se disponível e válido (evita sync desnecessário)
				if (
					selectedObjects &&
					Array.isArray(selectedObjects) &&
					selectedObjects.length > 0
				) {
					sendToTerminal(
						"✅ [EXPORT_PREP] Usando selectedObjects já disponíveis",
						{
							objetosCount: selectedObjects.length,
							hasStaticIds: selectedObjects.every((o: any) => o.staticId),
							hasGuids: selectedObjects.some((o: any) => o.guid),
						},
					);

					// Converter selectedObjects para o formato esperado (preservar dimensoes para validacao backend)
					objetos = selectedObjects.map((o: any) => ({
						id: o.id,
						staticId: o.staticId,
						guid: o.guid || null,
						nome: o.nome,
						quantity: o.quantity || 1,
						contraction: o.contraction || null,
						altura: o.height ?? null,
						largura: o.width ?? null,
					}));

					sourceUsed = "selectedObjects";
					sendToTerminal("✅ [EXPORT_PREP] DADOS_USADOS_DIRETAMENTE", {
						objetosCount: objetos.length,
						source: "selectedObjects",
					});
				} else {
					// Fallback: fazer sync apenas se selectedObjects não estiver disponível
					sendToTerminal(
						"⚠️ [EXPORT_PREP] selectedObjects não disponível - fazendo sync",
						{
							hasSelectedObjects: !!selectedObjects,
							selectedObjectsLength: selectedObjects?.length || 0,
							isArray: Array.isArray(selectedObjects),
						},
					);

					// Verificar se monitor está ativo
					sendToTerminal("🔍 [EXPORT_PREP] CHECKING_MONITOR_STATUS");
					const status = await monitoradorObjetos.verificarStatus();
					sendToTerminal("📊 [EXPORT_PREP] MONITOR_STATUS", {
						ativo: status?.ativo,
					});

					// Iniciar monitor se não estiver ativo
					if (!status?.ativo) {
						sendToTerminal("▶️ [EXPORT_PREP] STARTING_MONITOR");
						await monitoradorObjetos.iniciarComConfiguracao({
							pollingInterval: 400,
							batchSize: 10,
						});
						sendToTerminal("✅ [EXPORT_PREP] MONITOR_STARTED");
					} else {
						sendToTerminal("✅ [EXPORT_PREP] MONITOR_ALREADY_ACTIVE");
					}

					// Fazer sync
					sendToTerminal("🔄 [EXPORT_PREP] EXECUTING_SYNC");
					const syncRes = await monitoradorObjetos.sincronizarObjetos();

					// ✅ CORREÇÃO: sincronizarObjetos retorna MonitorSyncData diretamente
					// Se retornou dados (não lançou erro), o comando foi bem-sucedido
					objetos = syncRes?.objetos ?? [];

					sendToTerminal("📋 [EXPORT_PREP] SYNC_RESPONSE", {
						objetosCount: objetos.length,
						hasSyncRes: !!syncRes,
					});

					sourceUsed = "sync";
				}

				// Validação final (independente da fonte)
				if (!objetos.length) {
					sendToTerminal("❌ [EXPORT_PREP] VALIDATION_FAILED", {
						objetosCount: objetos.length,
						source: sourceUsed,
						hasSelectedObjects: !!selectedObjects,
						selectedObjectsLength: selectedObjects?.length || 0,
					});
					throw new Error("Nenhum objeto encontrado no documento");
				}

				sendToTerminal("✅ [EXPORT_PREP] SYNC_COMPLETE", {
					count: objetos.length,
					source: sourceUsed,
				});

				// Parar monitor apenas se fizemos sync (não precisa parar se usamos selectedObjects)
				if (sourceUsed === "sync") {
					sendToTerminal("⏸️ [EXPORT_PREP] STOPPING_MONITOR");
					await monitoradorObjetos.stopWithRetry(2, 2000);
					sendToTerminal("⏸️ [EXPORT_PREP] MONITOR_STOPPED");
				} else {
					sendToTerminal(
						"⏭️ [EXPORT_PREP] Monitor não parado (usou selectedObjects)",
					);
				}

				// ETAPA 2: PREPARAR DADOS
				setExportPrepareStep("prepare");
				sendToTerminal("🔧 [EXPORT_PREP] PREPARE_DATA");

				// Buscar regras
				let regrasForSave: any = selectedItem?.regras || {};
				if (!regrasForSave || Object.keys(regrasForSave).length === 0) {
					sendToTerminal("🔍 [EXPORT_PREP] FETCHING_RULES");
					try {
						// 🔎 DEBUG proId — dump completo de cada fonte candidata
						const itemProduto = selectedItem?.produto ?? null;
						const firstItem = selectedPedido?.itens?.[0] ?? null;
						const firstProduto = firstItem?.produto ?? null;

						const candItemProId = itemProduto?.pro_id;
						const candItemId = itemProduto?.id;
						const candFirstProId = firstProduto?.pro_id;
						const candFirstId = firstProduto?.id;

						sendToTerminal("🔎 [EXPORT_PREP] PRO_ID_DEBUG", {
							orcId: selectedPedido?.orc_id,
							oitId: selectedItem?.oit_id,
							selectedItemKeys: Object.keys(selectedItem || {}),
							selectedItemProduto: itemProduto,
							itemProdutoKeys: itemProduto ? Object.keys(itemProduto) : null,
							candidates: {
								"selectedItem.produto.pro_id": candItemProId,
								"selectedItem.produto.id": candItemId,
								"selectedPedido.itens[0].produto.pro_id": candFirstProId,
								"selectedPedido.itens[0].produto.id": candFirstId,
							},
							candidateTypes: {
								"selectedItem.produto.pro_id": typeof candItemProId,
								"selectedItem.produto.id": typeof candItemId,
								"selectedPedido.itens[0].produto.pro_id": typeof candFirstProId,
								"selectedPedido.itens[0].produto.id": typeof candFirstId,
							},
							firstItemKeys: firstItem ? Object.keys(firstItem) : null,
							firstProduto,
						});

						const rawPro = Number(candItemProId || candItemId || 0);
						const fallbackPro = Number(candFirstProId || candFirstId || 0);
						const proId = rawPro > 0 ? rawPro : fallbackPro;

						// Qual fonte efetivamente foi usada
						let proIdSource: string;
						if (candItemProId) proIdSource = "selectedItem.produto.pro_id";
						else if (candItemId) proIdSource = "selectedItem.produto.id";
						else if (candFirstProId)
							proIdSource = "selectedPedido.itens[0].produto.pro_id";
						else if (candFirstId)
							proIdSource = "selectedPedido.itens[0].produto.id";
						else proIdSource = "FALLBACK_0";

						const finalUrl = `/cdr/rules/producao/${proId}/${selectedPedido.orc_id}/${selectedItem.oit_id}`;

						sendToTerminal("📞 [EXPORT_PREP] API_RULES_REQUEST", {
							proId,
							proIdSource,
							rawPro,
							fallbackPro,
							orcId: selectedPedido.orc_id,
							oitId: selectedItem.oit_id,
							url: finalUrl,
						});

						// 🚨 Sanidade: alerta se proId parece suspeito (pequeno demais p/ pro_id real)
						if (!proId) {
							sendToTerminal("⚠️ [EXPORT_PREP] PRO_ID_ZERO_OR_MISSING");
						} else if (proId < 10) {
							sendToTerminal("⚠️ [EXPORT_PREP] PRO_ID_SUSPEITO_BAIXO", {
								proId,
								proIdSource,
								hint:
									"pro_id muito baixo — verifique se não está pegando id de categoria/tipo em vez do produto",
							});
						}

						const res = await api(finalUrl);
						const canon = normalizeRules(res?.data ?? res ?? {}, {
							variant: "producao",
						});
						regrasForSave = canon;
						sendToTerminal("✅ [EXPORT_PREP] RULES_LOADED", {
							hasDiretorios: !!regrasForSave?.diretorios,
						});
						debugLog("EXPORT:REGRAS_POPULADAS", {
							via: "producaoApi",
							hasDiretorios: !!regrasForSave?.diretorios,
						});
					} catch (error) {
						sendToTerminal("⚠️ [EXPORT_PREP] RULES_FETCH_FAILED", {
							error: error instanceof Error ? error.message : String(error),
						});
						debugLog("EXPORT:REGRAS_FALLBACK_FAILED", {
							message: error instanceof Error ? error.message : String(error),
						});
					}
				} else {
					sendToTerminal("✅ [EXPORT_PREP] RULES_FROM_ITEM", {
						hasRules: true,
					});
				}

				// Montar payload COM DADOS DO SYNC
				sendToTerminal("🔨 [EXPORT_PREP] BUILDING_PAYLOAD", {
					objetosCount: objetos.length,
				});

				// ✅ NORMALIZADO: Buscar ost_id em múltiplas fontes (mesma lógica do useExport)
				// Prioridade: status_orcamento.id > ost_id direto > orc_status_id > status.ost_id > status.id
				const ostId =
					(selectedPedido as any)?.status_orcamento?.id || // ✅ status_orcamento.id (novo formato)
					(selectedPedido as any)?.ost_id || // ✅ ost_id direto
					(selectedPedido as any)?.orc_status_id || // ✅ orc_status_id (legado)
					(selectedItem as any)?.ost_id || // ✅ ost_id no item
					(selectedItem as any)?.status?.ost_id || // ✅ status.ost_id no item
					(selectedItem as any)?.status?.id || // ✅ status.id no item
					(selectedPedido as any)?.status?.ost_id || // ✅ status.ost_id no pedido
					(selectedPedido as any)?.status?.id || // ✅ status.id no pedido
					null;

				// ✅ Log detalhado para debug quando ost_id não for encontrado
				if (!ostId) {
					sendToTerminal(
						"⚠️ [EXPORT_PREP] ost_id não encontrado em nenhuma fonte",
						{
							orcId: selectedPedido.orc_id,
							oitId: selectedItem.oit_id,
							pedidoKeys: Object.keys(selectedPedido || {}),
							itemKeys: Object.keys(selectedItem || {}),
							statusOrcamento: (selectedPedido as any)?.status_orcamento,
							pedidoOstId: (selectedPedido as any)?.ost_id,
							pedidoOrcStatusId: (selectedPedido as any)?.orc_status_id,
							itemOstId: (selectedItem as any)?.ost_id,
							itemStatus: (selectedItem as any)?.status,
							pedidoStatus: (selectedPedido as any)?.status,
						},
					);

					// ✅ Notificar usuário via toast (não-bloqueante)
					notify({
						type: "warning",
						title: "Status do Pedido Não Encontrado",
						message:
							"O status do pedido (ost_id) não foi encontrado. A movimentação automática pode falhar.",
						sticky: false,
					});
				} else {
					sendToTerminal("✅ [EXPORT_PREP] ost_id encontrado", {
						orcId: selectedPedido.orc_id,
						oitId: selectedItem.oit_id,
						ostId,
					});
				}

				const preExportData = {
					pedido: {
						orc_id: selectedPedido.orc_id,
						oit_id: selectedItem.oit_id,
						...(ostId ? { ost_id: ostId } : {}), // ✅ Incluir ost_id se encontrado
					},
					cliente: selectedPedido.cliente,
					produto: selectedItem.produto,
					material: selectedItem.material,
					regras: {
						diretorio_base_exportpng:
							regrasForSave?.diretorio_base_exportpng ??
							regrasForSave?.diretorios?.layout_png,
						diretorio_base_export_prod:
							regrasForSave?.diretorio_base_export_prod ??
							regrasForSave?.diretorios?.producao,
						diretorios: regrasForSave?.diretorios,
						horario_corte: regrasForSave?.horario_corte,
						seguranca: regrasForSave?.seguranca,
						padroes_cdr: regrasForSave?.padroes_cdr,
						// Dimensões máximas do material (para cálculo de escala do layout)
						altura_maxima_cm:
							regrasForSave?.altura_maxima_cm ??
							regrasForSave?.dimensoes?.altura_cm ??
							null,
						largura_maxima_cm:
							regrasForSave?.largura_maxima_cm ??
							regrasForSave?.dimensoes?.largura_cm ??
							null,
					},
					objetos: objetos.map((o: any) => ({
						id: o.id,
						staticId: o.staticId,
						guid: o.guid || null,
						nome: o.nome,
						quantity: o.quantity || 1,
						contraction: o.contraction || null,
						altura: o.altura ?? o.height ?? null,
						largura: o.largura ?? o.width ?? null,
					})),
					syncMeta: {
						synced: true,
						timestamp: Date.now(),
						total: objetos.length,
						source: "corel-sync",
					},
				} as any;

				sendToTerminal("✅ [EXPORT_PREP] PAYLOAD_BUILT", {
					hasRegras: !!preExportData.regras,
					hasDiretorios: !!preExportData.regras?.diretorios,
					objetosCount: preExportData.objetos.length,
					syncMeta: preExportData.syncMeta,
				});

				// Limpar dados antigos
				sendToTerminal("🧹 [EXPORT_PREP] CLEANING_OLD_DATA");
				try {
					storageClient.remove(Keys.export.currentSaveData, "session");
				} catch { }

				// Salvar novos dados
				sendToTerminal("💾 [EXPORT_PREP] SAVING_DATA");
				storageClient.set(Keys.export.currentSaveData, preExportData, {
					area: "session",
					ttlMs: 3600000,
				});

				// ✅ Salvar tipo de exportação selecionado para ExportarPage não mostrar seletor novamente
				const selectedType = exportType || "both";
				storageClient.set(Keys.export.selectedExportType, selectedType, {
					area: "session",
					ttlMs: 3600000,
				});

				sendToTerminal("💾 [EXPORT_PREP] DATA_SAVED", {
					objetosCount: objetos.length,
					synced: true,
					timestamp: preExportData.syncMeta.timestamp,
					exportType: selectedType,
				});

				// Resetar modals
				setShowExportLayoutModal(false);
				setShowLayoutOnlyPrompt(false);

				// ✅ NOVO: Se for apenas layout, executar diretamente sem navegar
				if (exportType === "layout") {
					sendToTerminal(
						"🟢 [EXPORT_PREP] Executando layout diretamente (sem navegar)",
						{
							objetosCount: objetos.length,
						},
					);

					try {
						// Preparar targets no formato esperado
						const targets = objetos.map((o: any) => ({
							staticId: o.staticId,
							guid: o.guid || "",
							multiplicador: o.quantity || 1,
							contracao: o.contraction || null,
						}));

						await executeLayoutDirectly(preExportData, targets);

						preparingExportRef.current = false; // ✅ Liberar lock ref
						setPreparingExport(false);
						sendToTerminal(
							"✅ [EXPORT_PREP] Layout executado diretamente com sucesso",
						);
						return;
					} catch (layoutError) {
						const errorMsg =
							layoutError instanceof Error
								? layoutError.message
								: String(layoutError);
						setExportPrepareError(errorMsg);
						preparingExportRef.current = false; // ✅ Liberar lock ref
						setPreparingExport(false);
						sendToTerminal(
							"❌ [EXPORT_PREP] Erro ao executar layout diretamente",
							{ error: errorMsg },
						);
						return;
					}
				}

				// Finalizar e navegar (para production ou both)
				// ✅ IMPORTANTE: Não liberar lock aqui - a navegação vai desmontar o componente
				// O lock será liberado pelo cleanup do useEffect ou ao voltar para esta tela
				setPreparingExport(false);
				sendToTerminal("✅ [EXPORT_PREP] COMPLETE", {
					totalDuration: Date.now() - preExportData.syncMeta.timestamp,
					nextStep: "navigate-to-export-page",
					exportType: exportType || "both",
				});

				// 🔍 DEBUG: Log ANTES de chamar onNavigateToExport
				sendToTerminal("🚀 [EXPORT_PREP] Calling onNavigateToExport...", {
					hasCallback: typeof onNavigateToExport === "function",
					callbackType: typeof onNavigateToExport,
				});

				try {
					onNavigateToExport();
					sendToTerminal(
						"✅ [EXPORT_PREP] onNavigateToExport called successfully",
					);
				} catch (navError) {
					sendToTerminal("❌ [EXPORT_PREP] onNavigateToExport THREW ERROR", {
						error:
							navError instanceof Error ? navError.message : String(navError),
						stack:
							navError instanceof Error
								? navError.stack?.slice(0, 500)
								: undefined,
					});
				}

				// ✅ Liberar lock APÓS navegação (pode não executar se componente desmontou)
				preparingExportRef.current = false;
			} catch (error) {
				const message =
					error instanceof Error
						? error.message
						: "Erro ao preparar exportação";
				setExportPrepareError(message);
				preparingExportRef.current = false; // ✅ Liberar lock ref em caso de erro
				setPreparingExport(false);
				sendToTerminal("❌ [EXPORT_PREP] FAILED", {
					error: message,
					step: exportPrepareStep,
					timestamp: Date.now(),
				});
			}
		},
		[
			// ✅ CORREÇÃO BUG DUPLO CLIQUE: Adicionado selectedObjects às dependências
			// Sem isso, a closure captura uma versão stale de selectedObjects
			preparingExport,
			resolveCaminhoArquivo,
			selectedItem,
			selectedPedido,
			selectedObjects, // ✅ ADICIONADO - crítico para evitar closure stale
			monitoradorObjetos,
			itemPendencias,
			onNavigateToExport,
			setShowExportLayoutModal,
			setShowLayoutOnlyPrompt,
			executeLayoutDirectly,
			notify,
		],
	);

	return {
		startProductionExport,
		preparingExport,
		exportPrepareStep,
		exportPrepareError,
	};
}
